import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, type Webhook, type WebhookDelivery } from '../lib/api';
import { useAsync } from '../lib/useAsync';

const EVENTS = ['participation.recorded', 'points.earned', 'tier.changed', 'fan.joined'];

/** Client backends subscribe here. The secret shows once; deliveries are signed HMAC-SHA256 (X-KK-Signature: t=…,v1=…). */
export function Webhooks() {
  const { orgId } = useParams();
  const org = useAsync(() => api.orgs.get(orgId!), [orgId]);
  const hooks = useAsync(() => api.webhooks.list(orgId!), [orgId]);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [created, setCreated] = useState<Webhook | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [error, setError] = useState('');

  async function create(e: FormEvent) {
    e.preventDefault(); setError('');
    try { setCreated(await api.webhooks.create(orgId!, { url, eventTypes: events, enabled: true })); setUrl(''); setEvents([]); hooks.reload(); } catch (err) { setError((err as Error).message); }
  }
  async function show(h: Webhook) {
    setOpen(h.id);
    setDeliveries(await api.webhooks.deliveries(orgId!, h.id));
  }

  return (
    <>
      <h1>{org.data?.name ?? '…'} <span className="muted" style={{ fontWeight: 400 }}>· Webhooks</span></h1>
      {created?.secret && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: '#86efac' }}>
          <b>Endpoint added.</b> Copy the signing secret now; it is not shown again.
          <p><code>{created.secret}</code></p>
          <p className="muted">Verify: HMAC-SHA256 of <code>{'{t}.{body}'}</code> with this secret equals <code>v1</code> in <code>X-KK-Signature: t=…,v1=…</code>.</p>
        </div>
      )}
      <form onSubmit={create} className="card stack" style={{ marginBottom: '1rem' }}>
        <div><label>HTTPS URL</label><input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://admin.tgrhaas.mmtestsystem.com/api/fandom/webhook" /></div>
        <div><label>Events (none = all)</label>
          <div className="row">{EVENTS.map((ev) => (
            <label key={ev} style={{ fontWeight: 500 }}><input type="checkbox" style={{ width: 'auto', marginRight: 6 }} checked={events.includes(ev)} onChange={(e) => setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))} />{ev}</label>
          ))}</div>
        </div>
        <div className="row"><button className="primary">Add endpoint</button>{error && <span className="err">{error}</span>}</div>
      </form>
      {hooks.data && hooks.data.length === 0 && <div className="empty">No endpoints yet.</div>}
      {hooks.data && hooks.data.length > 0 && (
        <table>
          <thead><tr><th>URL</th><th>Events</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {hooks.data.map((h) => (
              <tr key={h.id}>
                <td><code>{h.url}</code></td>
                <td className="muted">{h.eventTypes.length ? h.eventTypes.join(', ') : 'all'}</td>
                <td><span className={`pill ${h.enabled ? 'published' : 'closed'}`}>{h.enabled ? 'on' : 'off'}</span></td>
                <td className="row">
                  <button className="link" onClick={() => void show(h)}>Deliveries</button>
                  <button className="link" onClick={async () => { await api.webhooks.update(orgId!, h.id, { url: h.url, eventTypes: h.eventTypes, enabled: !h.enabled }); hooks.reload(); }}>{h.enabled ? 'Turn off' : 'Turn on'}</button>
                  <button className="link" onClick={async () => { await api.webhooks.update(orgId!, h.id, { url: h.url, eventTypes: [], enabled: h.enabled }); hooks.reload(); }}>All events</button>
                  <button className="link" onClick={async () => { await api.webhooks.remove(orgId!, h.id); hooks.reload(); if (open === h.id) setOpen(null); }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <>
          <h2>Recent deliveries</h2>
          {deliveries.length === 0 ? <div className="empty">Nothing delivered yet.</div> : (
            <table>
              <thead><tr><th>Event</th><th>Attempts</th><th>Delivered</th><th>Last error</th><th>Created</th></tr></thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td>{d.eventType}</td><td>{d.attempts}</td>
                    <td>{d.deliveredAt ? new Date(d.deliveredAt).toLocaleString() : <span className="muted">pending</span>}</td>
                    <td className="muted">{d.lastError ?? '—'}</td>
                    <td className="muted">{new Date(d.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}
